import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Card,
  Chip,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { ChevronDown, Lock, Server, ShieldOff } from 'lucide-react';
import {
  DeploymentTargetRecord,
  ServerLock,
  SetupRecord,
  SetupServerGroup,
  SetupStackSummary,
  SetupTargetSummary,
  StackCandidate,
} from '../../types/deployment';
import { CurrentSetupRow } from './CurrentSetupRow';
import { ServerStackSection } from './ServerStackSection';
import { SetupRunHistoryState } from './SetupHistoryPanel';
import {
  formatRelativeTime,
  getCustomerName,
  getLockTargetLabels,
  getServerGroupMetrics,
  getServerName,
} from './setupExplorerUtils';

interface ServerSetupGroupProps {
  group: SetupServerGroup;
  expanded: boolean;
  saving?: boolean;
  isAdmin: boolean;
  targetHistoryById: Record<string, SetupRunHistoryState | undefined>;
  stackHistoryByKey: Record<string, SetupRunHistoryState | undefined>;
  openTargetHistoryById: Record<string, boolean | undefined>;
  openStackHistoryByKey: Record<string, boolean | undefined>;
  onToggleGroup: () => void;
  onToggleTargetHistory: (summary: SetupTargetSummary) => void;
  onToggleStackHistory: (summary: SetupStackSummary) => void;
  onLoadMoreTargetHistory: (summary: SetupTargetSummary) => void;
  onRefreshTargetHistory: (summary: SetupTargetSummary) => void;
  onLoadMoreStackHistory: (summary: SetupStackSummary) => void;
  onRefreshStackHistory: (summary: SetupStackSummary) => void;
  onOpenSetup: (target: DeploymentTargetRecord) => void;
  onReprovision: (target: DeploymentTargetRecord) => void;
  onOpenManagedArtifacts: (target: DeploymentTargetRecord) => void;
  onTlsAction: (target: DeploymentTargetRecord, action: 'check' | 'issue' | 'renew-dry-run' | 'renew') => void;
  onRefreshSetup: (setupId: string) => void;
  onOpenRunDetails: (setup: SetupRecord) => void;
  onOpenForceRelease: (lock: ServerLock) => void;
  onOpenStack: (stack: StackCandidate) => void;
  onRetryStack: (stackRunId: string) => void;
}

export const ServerSetupGroup: React.FC<ServerSetupGroupProps> = ({
  group,
  expanded,
  saving = false,
  isAdmin,
  targetHistoryById,
  stackHistoryByKey,
  openTargetHistoryById,
  openStackHistoryByKey,
  onToggleGroup,
  onToggleTargetHistory,
  onToggleStackHistory,
  onLoadMoreTargetHistory,
  onRefreshTargetHistory,
  onLoadMoreStackHistory,
  onRefreshStackHistory,
  onOpenSetup,
  onReprovision,
  onOpenManagedArtifacts,
  onTlsAction,
  onRefreshSetup,
  onOpenRunDetails,
  onOpenForceRelease,
  onOpenStack,
  onRetryStack,
}) => {
  const metrics = getServerGroupMetrics(group);

  return (
    <Accordion expanded={expanded} onChange={onToggleGroup} disableGutters>
      <AccordionSummary expandIcon={<ChevronDown size={18} />}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{ md: 'center' }}
          sx={{ width: '100%' }}
        >
          <Stack spacing={0.4}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Server size={16} />
              <Typography variant="subtitle1" fontWeight={700}>
                {getServerName(group.server)}
              </Typography>
              <Chip size="small" variant="outlined" label={getCustomerName(group.customer)} />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {metrics.targetCount} targets • {metrics.stackCount} stack lanes • Last activity {formatRelativeTime(group.latestActivityAt)}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {metrics.lockCount > 0 ? (
              <Chip size="small" color="warning" label={`${metrics.lockCount} locks`} />
            ) : null}
            {metrics.activeSetups > 0 ? (
              <Chip size="small" color="info" label={`${metrics.activeSetups} active`} />
            ) : null}
            {metrics.failedTargets > 0 ? (
              <Chip size="small" color="error" label={`${metrics.failedTargets} target issues`} />
            ) : null}
            {metrics.failedStacks > 0 ? (
              <Chip size="small" color="error" label={`${metrics.failedStacks} stack issues`} />
            ) : null}
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1.5}>
          {group.activeLocks.length > 0 ? (
            <Card variant="outlined" sx={{ px: 2, py: 1.5 }}>
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Lock size={16} />
                  <Typography variant="subtitle2" fontWeight={700}>Active Locks</Typography>
                </Stack>
                <Stack spacing={1}>
                  {group.activeLocks.map((lock) => {
                    const targetLabels = getLockTargetLabels(lock);
                    return (
                      <Stack
                        key={lock._id}
                        direction={{ xs: 'column', md: 'row' }}
                        justifyContent="space-between"
                        spacing={1.5}
                        alignItems={{ md: 'center' }}
                      >
                        <Stack spacing={0.25}>
                          <Typography variant="body2" fontWeight={600}>
                            {lock.runType}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {targetLabels.length > 0 ? targetLabels.join(', ') : 'Entire server'} • Held by {lock.owner?.name || lock.owner?.email || 'unknown'} • Since {formatRelativeTime(lock.acquiredAt)}
                          </Typography>
                        </Stack>
                        {isAdmin ? (
                          <Tooltip title="Admin: force-release this lock">
                            <span>
                              <Button
                                size="small"
                                color="error"
                                variant="outlined"
                                startIcon={<ShieldOff size={14} />}
                                onClick={() => onOpenForceRelease(lock)}
                              >
                                Force Release
                              </Button>
                            </span>
                          </Tooltip>
                        ) : null}
                      </Stack>
                    );
                  })}
                </Stack>
              </Stack>
            </Card>
          ) : null}

          <Stack spacing={1.5}>
            {group.targetSummaries.map((summary) => (
              <CurrentSetupRow
                key={summary.targetId}
                summary={summary}
                saving={saving}
                isAdmin={isAdmin}
                historyOpen={Boolean(openTargetHistoryById[summary.targetId])}
                historyState={targetHistoryById[summary.targetId]}
                onToggleHistory={() => onToggleTargetHistory(summary)}
                onLoadMoreHistory={() => onLoadMoreTargetHistory(summary)}
                onRefreshHistory={() => onRefreshTargetHistory(summary)}
                onOpenSetup={onOpenSetup}
                onReprovision={onReprovision}
                onOpenManagedArtifacts={onOpenManagedArtifacts}
                onTlsAction={onTlsAction}
                onRefreshSetup={onRefreshSetup}
                onOpenRunDetails={onOpenRunDetails}
              />
            ))}
          </Stack>

          <ServerStackSection
            stackSummaries={group.stackSummaries}
            saving={saving}
            historyStateByKey={stackHistoryByKey}
            openHistoryByKey={openStackHistoryByKey}
            onToggleHistory={onToggleStackHistory}
            onLoadMoreHistory={onLoadMoreStackHistory}
            onRefreshHistory={onRefreshStackHistory}
            onOpenStack={onOpenStack}
            onRetryStack={onRetryStack}
            onOpenRunDetails={onOpenRunDetails}
          />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
